import { trpc } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { LOGIN_PATH } from "@/const";
import { clearPatientIdRevealCache } from "@/hooks/usePatientIdLabel";
import {
  clearPatientIdDek,
  setPatientIdDekFromBootstrap,
} from "@/lib/patient-id-dek";

/** Logged-out bootstrap shape — keeps useAuth.isAuthenticated false after cache clear. */
export const LOGGED_OUT_BOOTSTRAP = {
  user: null,
  profile: null,
  onboarding: null,
  patientIdDek: null,
} as const;

export type LogoutOptions = {
  /** When true, bumps tokenVersion so every device session dies. */
  everywhere?: boolean;
};

/** Location state handed to `/login` after a deliberate sign-out. */
export type SignedOutLocationState = {
  signedOut: true;
  everywhere?: boolean;
};

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath = LOGIN_PATH } =
    options ?? {};

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const everywhereRef = useRef(false);

  const utils = trpc.useUtils();

  const {
    data: bootstrap,
    isLoading,
    error,
    refetch,
  } = trpc.auth.bootstrap.useQuery(undefined, {
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const user = bootstrap?.user;
  const profile = bootstrap?.profile ?? null;
  const onboarding = bootstrap?.onboarding ?? null;
  const patientIdDek = bootstrap?.patientIdDek ?? null;

  useEffect(() => {
    setPatientIdDekFromBootstrap(patientIdDek);
  }, [patientIdDek]);

  const finishLogout = useCallback(async () => {
    // Drop every cached query (surgeries, profile, …) then re-seed bootstrap
    // as logged-out. Invalidating alone is not enough: with the old authed
    // bootstrap, UNAUTHORIZED left the previous user in `data` and Login
    // redirected straight back into the app.
    const everywhere = everywhereRef.current;
    clearPatientIdDek();
    clearPatientIdRevealCache();
    await queryClient.cancelQueries();
    queryClient.clear();
    utils.auth.bootstrap.setData(undefined, LOGGED_OUT_BOOTSTRAP);
    // replace: avoid Back landing on a gated route that immediately
    // bounces through RequireAuth → login again.
    const state: SignedOutLocationState = everywhere
      ? { signedOut: true, everywhere: true }
      : { signedOut: true };
    navigate(redirectPath, { replace: true, state });
  }, [queryClient, utils.auth.bootstrap, navigate, redirectPath]);

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      void finishLogout();
    },
    onError: () => {
      // Cookie may already be gone or the request failed — still leave the UI.
      // If the cookie is still valid, the next bootstrap refetch restores the session.
      void finishLogout();
    },
  });

  const logoutAllMutation = trpc.auth.logoutAll.useMutation({
    onSuccess: () => {
      void finishLogout();
    },
    onError: () => {
      void finishLogout();
    },
  });

  const isSigningOut =
    logoutMutation.isPending || logoutAllMutation.isPending;

  const logout = useCallback(
    (opts?: LogoutOptions) => {
      if (isSigningOut) return;
      everywhereRef.current = opts?.everywhere === true;
      if (everywhereRef.current) logoutAllMutation.mutate();
      else logoutMutation.mutate();
    },
    [isSigningOut, logoutMutation, logoutAllMutation],
  );

  useEffect(() => {
    if (redirectOnUnauthenticated && !isLoading && !user) {
      const currentPath = window.location.pathname;
      if (currentPath !== redirectPath) {
        navigate(redirectPath);
      }
    }
  }, [redirectOnUnauthenticated, isLoading, user, navigate, redirectPath]);

  return useMemo(
    () => ({
      user: user ?? null,
      profile,
      onboarding,
      isAuthenticated: !!user,
      // Keep bootstrap loading separate from sign-out so dialogs/menus do not
      // unmount into the auth skeleton while the session is tearing down.
      isLoading,
      isSigningOut,
      error,
      logout,
      refresh: refetch,
    }),
    [
      user,
      profile,
      onboarding,
      isLoading,
      isSigningOut,
      error,
      logout,
      refetch,
    ],
  );
}
